import { useState, useEffect } from 'react';
import apiClient from '../utils/api';
import { fetchStaticTermsMarkdown } from '../utils/legalPageContent';

const renderTermsMarkdown = (md) => {
  if (!md) return "";
  const esc = (t) =>
    String(t)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  const inline = (t) =>
    esc(t)
      .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
      .replace(/`([^`]+)`/g, "<code>$1</code>");
  const out = [];
  let inUl = false;
  let tableLines = null;
  const flushUl = () => {
    if (inUl) {
      out.push("</ul>");
      inUl = false;
    }
  };
  const flushTable = () => {
    if (!tableLines) return;
    const rows = tableLines.filter((r) => !/^\|[\s\-:|]+\|$/.test(r.trim()));
    if (rows.length) {
      out.push('<table class="terms-table"><tbody>');
      rows.forEach((row, i) => {
        const cells = row.split("|").slice(1, -1).map((c) => c.trim());
        const tag = i === 0 ? "th" : "td";
        out.push(`<tr>${cells.map((c) => `<${tag}>${inline(c)}</${tag}>`).join("")}</tr>`);
      });
      out.push("</tbody></table>");
    }
    tableLines = null;
  };
  md.split("\n").forEach((line) => {
    const trimmed = line.trim();
    if (trimmed.startsWith("|")) {
      flushUl();
      if (!tableLines) tableLines = [];
      tableLines.push(trimmed);
      return;
    }
    flushTable();
    if (trimmed === "---") {
      flushUl();
      out.push("<hr />");
      return;
    }
    if (trimmed.startsWith("### ")) {
      flushUl();
      out.push(`<h3>${inline(trimmed.slice(4))}</h3>`);
      return;
    }
    if (trimmed.startsWith("## ")) {
      flushUl();
      out.push(`<h2>${inline(trimmed.slice(3))}</h2>`);
      return;
    }
    if (trimmed.startsWith("# ")) {
      flushUl();
      out.push(`<h1>${inline(trimmed.slice(2))}</h1>`);
      return;
    }
    if (trimmed.startsWith("> ")) {
      flushUl();
      out.push(`<blockquote><p>${inline(trimmed.slice(2))}</p></blockquote>`);
      return;
    }
    if (trimmed.startsWith("- ")) {
      if (!inUl) {
        out.push("<ul>");
        inUl = true;
      }
      out.push(`<li>${inline(trimmed.slice(2))}</li>`);
      return;
    }
    if (!trimmed) {
      flushUl();
      return;
    }
    flushUl();
    out.push(`<p>${inline(trimmed)}</p>`);
  });
  flushUl();
  flushTable();
  return out.join("\n");
};

const TermsPage = ({ setPage }) => {
  const [bodyHtml, setBodyHtml] = useState("");
  const [loadError, setLoadError] = useState("");
  useEffect(() => {
    window.scrollTo(0, 0);
    let cancelled = false;

    const loadStaticTerms = () =>
      fetchStaticTermsMarkdown().then((text) => {
        if (!cancelled) setBodyHtml(renderTermsMarkdown(text));
      });

    apiClient
      .getLegalPage('terms-of-service')
      .then((res) => {
        if (cancelled) return;
        const body = res.data?.body;
        if (body?.trim()) {
          setBodyHtml(renderTermsMarkdown(body));
          return;
        }
        return loadStaticTerms();
      })
      .catch(() => {
        if (!cancelled) return loadStaticTerms();
        return undefined;
      })
      .catch((e) => {
        if (!cancelled) setLoadError(e.message || "Failed to load terms.");
      });

    return () => {
      cancelled = true;
    };
  }, []);
  return (
    <div className="container terms-page">
      <button type="button" className="btn btn-ghost btn-sm" style={{ marginBottom: "1rem" }} onClick={() => setPage("home")}>
        <i className="fas fa-arrow-left"></i> Back
      </button>
      {loadError ? (
        <p className="terms-loading">{loadError}</p>
      ) : bodyHtml ? (
        <div className="terms-body" dangerouslySetInnerHTML={{ __html: bodyHtml }} />
      ) : (
        <p className="terms-loading">Loading Terms of Service…</p>
      )}
    </div>
  );
};

export { renderTermsMarkdown };
export default TermsPage;
